// @flow
import { klona } from "klona/full";
import pick from "lodash/pick";

import { has, isPlainObject } from "../../utils/helpers/objects";

/**
 * This util helps to normalize various attribute data formats available in the modular ui.
 * It fixes differences in choice attributes with dynamic schema's, links on download attributes, composites with various children, etc
 *
 * @private
 */
class AttributeDataHelper {
  _key: string;
  _attribute: Object;
  _value: any;
  _children: Array<AttributeDataHelper>;

  /**
   */
  constructor(
    data: Object | Array<Object>,
    key: string,
    childrenKeys: Array<Object>,
  ) {
    this._key = key;

    if (Array.isArray(data)) {
      this._attribute =
        data.find(
          (attr) =>
            attr.elementid === key || attr.name === key || attr.param === key,
        ) || {};
      this._value = this.getValue(this._attribute);
    } else {
      const attributeData = klona(data);
      attributeData._links = pick(data._links, [
        "concept",
        "download",
        "lookupOptions",
        "lookupList",
      ]);

      this._attribute = attributeData;

      if (data[key] === undefined || ("name" in data && "param" in data)) {
        this._value = this.getValue(data);
      } else {
        this._value = data[key];
      }
    }

    this._children = this.createChildren(data, childrenKeys);
  }

  /**
   */
  childData(
    data: Object | Array<Object>,
    parentKey: string,
    childKey: string,
  ): Object {
    if (Array.isArray(data)) {
      if (Array.isArray(this._attribute.elements)) {
        return this._attribute.elements.find(
          (element) => element.elementid === childKey,
        );
      }
    } else if (has(data, parentKey) && isPlainObject(data[parentKey])) {
      const parentData = data[parentKey];
      if (isPlainObject(parentData) && data.content?.[childKey]) {
        return { ...parentData, content: data.content[childKey] };
      }
      return parentData;
    } else if (has(data, childKey) && isPlainObject(data[childKey])) {
      return data[childKey];
    }

    if (isPlainObject(data)) {
      return data;
    }

    return {};
  }

  /**
   */
  createChild(
    data: Object | Array<Object>,
    childData: Object,
    keyObject: Object,
  ): AttributeDataHelper {
    const dynamicschema =
      isPlainObject(data) && !Array.isArray(data)
        ? data.dynamicschema
        : undefined;

    let dynamicschemaId = null;
    if (keyObject.dynamicschemaId) {
      dynamicschemaId = keyObject.dynamicschemaId;
    } else if (
      isPlainObject(data) &&
      typeof data.key === "string" &&
      keyObject.key
    ) {
      dynamicschemaId = `${data.key}.${keyObject.key}`;
    }

    return new AttributeDataHelper(
      {
        ...childData,
        dynamicschema,
        dynamicschemaId,
      },
      keyObject.key,
      keyObject.children,
    );
  }

  /**
   */
  createChildren(
    data: Object | Array<Object>,
    childrenKeys: Array<Object> = [],
  ): Array<AttributeDataHelper> {
    // Check if the original data explicitly contains the key and is explicitly null.
    const keyExplicitlyNull = this.value === null;

    if (keyExplicitlyNull) {
      // Handling for when the value is explicitly null: set childData to null.
      return childrenKeys.map((keyObject) => {
        const childData = null;
        return this.createChild(data, childData, keyObject);
      });
    }

    const childrenIsArray = Array.isArray(this.value);

    if (childrenIsArray) {
      return this.value.map((value) =>
        childrenKeys.map((keyObject) => {
          const childData = this.childData(value, this.key, keyObject.key);

          return this.createChild(data, childData, keyObject);
        }),
      );
    }

    return childrenKeys.map((keyObject) => {
      const childData = this.childData(data, this.key, keyObject.key);

      return this.createChild(data, childData, keyObject);
    });
  }

  /**
   */
  getValue(attribute: Object): any {
    if ("values" in attribute) {
      return attribute.values;
    }

    if ("value" in attribute) {
      return attribute.value;
    }

    if ("suggestions" in attribute) {
      return attribute.suggestions;
    }

    if ("suggestion" in attribute) {
      return attribute.suggestion;
    }

    if (Array.isArray(attribute.options)) {
      return attribute.options
        .filter((option) => option.selected)
        .map((option) => option.code || option.key);
    }

    return undefined;
  }

  /**
   */
  get key(): string {
    return this._key;
  }

  /**
   */
  get value(): any {
    return this._value;
  }

  /**
   */
  get static(): boolean {
    return this._attribute.static || false;
  }

  /**
   */
  get links(): any {
    return this._attribute._links || void 0;
  }

  /**
   */
  get dynamicschemaId(): string {
    return this._attribute.dynamicschemaId || this.key;
  }

  /*
    retrieve the dynamischema by the dynamischemaId
    mentioned in the contributions of the attribute
  */
  /**
   */
  get dynamicschema(): any {
    const { dynamicschema } = this._attribute;
    if (!dynamicschema) {
      return void 0;
    }

    if (Array.isArray(dynamicschema)) {
      return dynamicschema;
    } else if (dynamicschema[this.dynamicschemaId]) {
      const attrDS = dynamicschema[this.dynamicschemaId];
      return attrDS.map((attr) => {
        if (attr.elements) {
          return {
            ...attr,
            elements: {
              ...attr.elements,
              dynamicschema,
            },
          };
        }

        return attr;
      });
    }

    return void 0;
  }

  /**
   */
  get options(): any {
    return this._attribute.options || void 0;
  }

  /**
   */
  get message(): any {
    return this._attribute.message || void 0;
  }

  /**
   */
  get isResult(): boolean {
    return this._attribute.isResult || false;
  }

  /**
   */
  get referenceDate(): any {
    return this._attribute.referenceDate || void 0;
  }

  /**
   */
  get children(): Array<AttributeDataHelper> {
    return this._children || [];
  }

  /**
   */
  get content(): Object {
    if (this._attribute.content) {
      const pickedContent = pick(this._attribute.content, [
        "header",
        "label",
        "elements",
        "layouthint",
        "text",
      ]);

      if (Object.keys(pickedContent).length > 0) {
        return pickedContent;
      }
    }

    return null;
  }

  /**
   */
  getData(): Object {
    const value = this.value === undefined ? null : this.value;
    return {
      key: this.key,
      value: value,
      static: this.static,
      _links: this.links,
      dynamicschema: this.dynamicschema,
      dynamicschemaId: this.dynamicschemaId,
      options: this.options,
      message: this.message,
      isResult: this.isResult,
      referenceDate: this.referenceDate,
      children: this.children.map((child) =>
        Array.isArray(child)
          ? child.map((subChild) => subChild.getData())
          : child.getData(),
      ),
      content: this.content,
    };
  }
}

export default AttributeDataHelper;
